<template>
  <div
    ref="rootRef"
    class="clamp-text"
  >
    <textarea
      v-if="editing"
      ref="areaRef"
      :value="modelValue"
      class="form-control clamp-text__area"
      rows="1"
      @input="onInput"
      @blur="onAreaBlur"
    />
    <span
      v-if="!editing && modelValue"
      ref="measureRef"
      class="clamp-text__measure"
      aria-hidden="true"
    >{{ modelValue }}</span>
    <!-- В ридонли fieldset[disabled] глушит button: лицо поля — div, клик раскрывает текст. -->
    <component
      :is="locked ? 'div' : 'button'"
      v-if="!editing"
      type="button"
      class="form-control clamp-text__face"
      :class="{
        'is-readonly': locked,
        'clamp-text__face--expanded': expanded,
        'clamp-text__face--interactive': interactive,
      }"
      :role="locked ? 'button' : undefined"
      :tabindex="locked ? (interactive ? 0 : -1) : undefined"
      :aria-expanded="locked ? String(expanded) : undefined"
      @click="onFaceClick"
      @keydown.enter.prevent="onFaceClick"
      @keydown.space.prevent="onFaceClick"
      @blur="onFaceBlur"
    ><span class="clamp-text__text">{{ modelValue }}</span></component>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

/**
 * Поле текста до 3 строк с «…».
 * Клик раскрывает целиком (просмотр и редактирование).
 * В просмотре повторный клик по полю сворачивает его; иначе — когда фокус уходит.
 */
const props = defineProps({
  modelValue: { type: String, default: '' },
  /** Карточка не в режиме редактирования: клик только раскрывает текст. */
  locked: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const rootRef = ref(null)
const areaRef = ref(null)
const measureRef = ref(null)
const editing = ref(false)
const expanded = ref(false)
const overflowing = ref(false)

const interactive = computed(() => !props.locked || overflowing.value || expanded.value)

/** Подгоняет textarea под весь текст. */
function resizeArea() {
  const el = areaRef.value
  if (!el) {
    return
  }
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

/** Есть ли текст сверх трёх строк в свёрнутом виде. */
function measureOverflow() {
  if (editing.value || expanded.value) {
    return
  }
  const face = rootRef.value?.querySelector('.clamp-text__face')
  const measure = measureRef.value
  if (!face || !measure) {
    overflowing.value = false
    return
  }
  overflowing.value = measure.offsetHeight > face.clientHeight + 1
}

function scheduleMeasure() {
  nextTick(() => {
    requestAnimationFrame(() => measureOverflow())
  })
}

function collapse() {
  editing.value = false
  expanded.value = false
  scheduleMeasure()
}

function openEditor() {
  editing.value = true
  expanded.value = true
  nextTick(() => {
    const el = areaRef.value
    if (!el) {
      return
    }
    el.focus()
    resizeArea()
  })
}

function onFaceClick() {
  if (!props.locked) {
    openEditor()
    return
  }
  if (expanded.value) {
    collapse()
    return
  }
  if (overflowing.value) {
    expanded.value = true
  }
}

function onFaceBlur(event) {
  if (rootRef.value?.contains(event.relatedTarget)) {
    return
  }
  if (props.locked && expanded.value) {
    collapse()
  }
}

function onAreaBlur(event) {
  if (rootRef.value?.contains(event.relatedTarget)) {
    return
  }
  // После blur, чтобы клик по следующему полю успел встать в фокус.
  setTimeout(() => {
    if (rootRef.value?.contains(document.activeElement)) {
      return
    }
    collapse()
  }, 0)
}

function onInput(event) {
  emit('update:modelValue', event.target.value)
  resizeArea()
}

/** В просмотре клик в другое место сворачивает раскрытый текст. */
function onDocPointerDown(event) {
  if (editing.value || !expanded.value) {
    return
  }
  if (rootRef.value?.contains(event.target)) {
    return
  }
  collapse()
}

watch(() => props.modelValue, () => {
  if (editing.value) {
    nextTick(resizeArea)
    return
  }
  scheduleMeasure()
})

watch(() => props.locked, locked => {
  if (locked && editing.value) {
    collapse()
  }
})

let resizeObserver = null

onMounted(() => {
  scheduleMeasure()
  document.addEventListener('pointerdown', onDocPointerDown, true)
  const root = rootRef.value
  if (root && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => measureOverflow())
    resizeObserver.observe(root)
  }
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', onDocPointerDown, true)
  resizeObserver?.disconnect()
  resizeObserver = null
})

defineExpose({ measure: scheduleMeasure })
</script>

<style scoped>
.clamp-text {
  --clamp-font: 0.85rem;
  --clamp-lh: calc(var(--clamp-font) * 1.25);
  --clamp-lines: 3;
  --clamp-pad-y: 0.4rem;
  position: relative;
  min-width: 0;
  width: 100%;
}

.clamp-text__measure {
  position: absolute;
  left: 0;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  width: 100%;
  height: auto;
  max-height: none;
  padding: var(--clamp-pad-y) 0.75rem;
  line-height: var(--clamp-lh);
  font-size: var(--clamp-font);
  white-space: pre-wrap;
  word-break: break-word;
  overflow: visible;
}

.clamp-text__face.form-control {
  box-sizing: border-box;
  height: auto;
  min-height: 36px;
  /* Ровно 3 строки: line-height длиной, без доли 4-й строки от em/padding. */
  max-height: calc(var(--clamp-lh) * var(--clamp-lines) + var(--clamp-pad-y) * 2 + 2px);
  padding: var(--clamp-pad-y) 0.75rem;
  font-size: var(--clamp-font);
  line-height: 0;
  overflow: hidden;
  text-align: left;
  cursor: default;
}

.clamp-text__text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  max-height: calc(var(--clamp-lh) * var(--clamp-lines));
  font-size: var(--clamp-font);
  line-height: var(--clamp-lh);
  white-space: pre-wrap;
  word-break: break-word;
  text-overflow: ellipsis;
}

.clamp-text__face--interactive,
.clamp-text__face--interactive.is-readonly {
  cursor: pointer;
  pointer-events: auto;
}

.clamp-text__face--expanded.form-control {
  display: block;
  max-height: none;
  overflow: visible;
  line-height: var(--clamp-lh);

  .clamp-text__text {
    display: block;
    -webkit-line-clamp: unset;
    max-height: none;
    overflow: visible;
  }
}

.clamp-text__area.form-control {
  height: auto;
  min-height: 36px;
  max-height: none;
  padding: 0.4rem 0.75rem;
  line-height: 1.25;
  resize: none;
  overflow: hidden;
  white-space: pre-wrap;
  field-sizing: content;
}
</style>
